import { ThemeEvents, VariantUpdateEvent } from '@theme/events'

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog')
    if (!closestSection) return
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice)
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog')
    if (!closestSection) return
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice)
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return
    }

    // Try to scope the lookup to the same product-price element using identifiers
    let productPriceSelector = 'product-price'
    if (this.dataset.blockId) {
      productPriceSelector += `[data-block-id="${this.dataset.blockId}"]`
    } else if (this.dataset.productId) {
      productPriceSelector += `[data-product-id="${this.dataset.productId}"]`
    }

    const newProductPriceEl =
      event.detail.data.html.querySelector(productPriceSelector) ||
      event.detail.data.html.querySelector('product-price')

    const newPrice = newProductPriceEl?.querySelector('[ref="priceContainer"]') ||
      event.detail.data.html.querySelector('product-price [ref="priceContainer"]')
    const currentPrice = this.querySelector('[ref="priceContainer"]')

    if (!newPrice || !currentPrice) return

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      currentPrice.replaceWith(newPrice)
    }

    // Also sync the deposit note (if present) which is rendered outside the price container
    const newDeposit = newProductPriceEl?.querySelector('.deposit-note') ||
      event.detail.data.html.querySelector('product-price .deposit-note')
    const currentDeposit = this.querySelector('.deposit-note')

    if (newDeposit && currentDeposit) {
      if (currentDeposit.innerHTML !== newDeposit.innerHTML) {
        currentDeposit.replaceWith(newDeposit)
      }
    } else if (newDeposit && !currentDeposit) {
      // Insert the deposit note right after the price container to keep layout consistent
      currentPrice.insertAdjacentElement('afterend', newDeposit)
    } else if (!newDeposit && currentDeposit) {
      currentDeposit.remove()
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice)
}
